import { Component, computed, effect, input, model, output, signal, untracked } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DrawerModule } from "primeng/drawer";
import { Area, JobIssue, Media, SyncableStatus } from "../../../../../../core/database/db";
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { ImagesModalComponent } from "../../../../../media/components/images-modal/images-modal.component";
import { BqaImageItem } from "../../../../../media/components/images-modal/bqa-image-item";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { Dialog } from "@angular/cdk/dialog";
import { UuidService } from "../../../../../../shared/services/uuid.service";
import { ASSIGN_TO_ME_OPTION_LABEL, SELF_ASSIGNED_VENDOR, VendorDTO, withAssignToMeOption } from "../../../../../vendors/models/VendorDTO";
import { BlobService } from "../../../../../../shared/services/blob.service";
import { ImageCompressionPipelineService, MediaForCompression } from "../../../../../media/services/image-compression-pipeline.service";
import { insertAt, orderSavedAndStagedMedia } from "../../../../../media/utils/media-order.util";
import { CONSTANTS } from "../../../../../../constants";
import { ADD_NEW_ROOM_AREA, UNASSIGNED_AREA } from "../../../../../areas/models/AreaDTO";
import { AddAreaDialogComponent, AddAreaSubmit } from "../../../job-areas/add-area/add-area-dialog.component";
import { FormFieldComponent } from "../../../../../../shared/components/form-field/form-field.component";
import { ModalIcon, UiModalService } from "../../../../../../shared/components/modal/ui-modal.service";
import { PhotoCaptureComponent } from "../../../../../../shared/components/photo-capture/photo-capture.component";
import { SectionHeaderComponent } from "../../../../../../shared/components/section-header/section-header.component";
import { SelectFieldComponent } from "../../../../../../shared/components/select-field/select-field.component";
import { SeverityInfoComponent } from "../../../../../../shared/components/severity-info/severity-info.component";
import { StandardBottomSheetComponent } from "../../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { TextareaComponent } from "../../../../../../shared/components/textarea/textarea.component";
import { VerticalButtonGroupComponent, VerticalButton } from "../../../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { UiToastService } from "../../../../../../shared/components/toast/ui-toast.service";
import { LoggerService } from "../../../../../../shared/services/logger.service";
import { BqInspectionDiscussAndDemonstrateDataService } from "../../../../services/bq-inspection-discuss-and-demonstrate-data.service";

export type DiscussAndDemonstrateIssueSaveMode = "return" | "addAnother";

/**
 * BQ1 ("Discuss")/BQ5 ("Demonstrate")/BQ8 punchlist item drawer. Every read (rooms, vendors, an
 * issue's existing photos/vendor) comes from BqInspectionDiscussAndDemonstrateDataService's
 * already-loaded signals, and every write (save/delete) goes through that same service - this
 * component never touches IndexedDB or any repository service directly.
 */
@Component({
  selector: "app-discuss-and-demonstrate-issue-drawer",
  standalone: true,
  imports: [
    DrawerModule,
    FormsModule,
    ReactiveFormsModule,
    StandardBottomSheetComponent,
    SectionHeaderComponent,
    FormFieldComponent,
    SelectFieldComponent,
    SeverityInfoComponent,
    TextareaComponent,
    PhotoCaptureComponent,
    VerticalButtonGroupComponent,
    AddAreaDialogComponent
  ],
  templateUrl: "./discuss-and-demonstrate-issue-drawer.component.html",
  styleUrl: "./discuss-and-demonstrate-issue-drawer.component.scss"
})
export class DiscussAndDemonstrateIssueDrawerComponent {
  private readonly unassignedAreaIdentifier = UNASSIGNED_AREA.identifier;

  isOpen = model<boolean>(false);
  issue = input<JobIssue | undefined>(undefined);
  /** Room to pre-select when creating a new issue (e.g. the last room used on this job); ignored in edit mode, where the room comes from the issue being edited instead. */
  initialLotAreaIdentifier = input<string | undefined>(undefined);

  readonly saved = output<{ issue: JobIssue; newIssue: boolean; saveMode: DiscussAndDemonstrateIssueSaveMode }>();
  readonly closed = output<void>();
  readonly deleted = output<void>();

  protected savingMode = signal<DiscussAndDemonstrateIssueSaveMode | null>(null);
  protected displayUnassignedRoomMessage: boolean = false;
  private existingImages = signal<Media[]>([]);
  private addedImages = signal<Media[]>([]);
  /** Annotated copies of saved photos (copy fileIdentifier -> original's id), shown in the original's slot. */
  private annotationAnchors = signal<ReadonlyMap<string, number>>(new Map());

  private allImagesRaw = computed(() =>
    orderSavedAndStagedMedia(this.existingImages(), this.addedImages(), this.annotationAnchors())
      .filter(im => !!im.deleted === false)
      .reverse());
  protected allImages = computed<SafeUrl[]>(() =>
    this.allImagesRaw()
      .map(img => {
        if (img.base64Image) {
          return this.createObjectUrlAndTrack(this.blobService.base64ToBlob(img.base64Image));
        }
        return img.resourceUri ?? "";
      })
      .filter(uri => !!uri)
      .map(uri => this.sanitizer.bypassSecurityTrustUrl(uri))
  );

  private galleryItems = computed(() =>
    this.allImagesRaw()
      .filter(image => (image.base64Image || image.resourceUri) && !image.deleted)
      .map(image => {
        let azureBlobUri = "";

        if (image.base64Image) {
          azureBlobUri = this.createObjectUrlAndTrack(this.blobService.base64ToBlob(image.base64Image));
        } else if (image.resourceUri) {
          azureBlobUri = image.resourceUri;
        }

        return new BqaImageItem({
          azureBlobUri: azureBlobUri || "",
          mediaIdentifier: image.fileIdentifier,
          isThumbnailOnly: image.isThumbnailOnly || false
        });
      })
  );

  /** Only the steps that load lot rooms (BQ1/BQ5) let the CM create one mid-inspection. */
  protected readonly canAddRoom = computed(() => this.bqStepDataService.availableRooms() !== undefined);

  /** Rooms available for assignment - sourced from the already-loaded step data, no fresh IndexedDB reads. */
  protected readonly areas = computed<Area[]>(() => {
    const rooms = [...(this.bqStepDataService.availableRooms() ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    const options = [...rooms, UNASSIGNED_AREA];

    if (this.canAddRoom()) {
      options.push(ADD_NEW_ROOM_AREA);
    }

    return options;
  });

  /** Room types offered by the Add a Room sheet. */
  protected readonly areasConfig = computed(() => this.bqStepDataService.areasConfig());
  /** Zones the new room can belong to (Interior/Exterior). */
  protected readonly zoneOptions = computed(() => this.bqStepDataService.lotZones() ?? []);

  /** Vendors available for assignment - sourced from the already-loaded step data, with "Assign to Me" pinned first. */
  protected readonly vendors = computed<VendorDTO[]>(() =>
    withAssignToMeOption((this.bqStepDataService.vendors() ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)))
  );
  protected readonly selfAssignedVendorId = SELF_ASSIGNED_VENDOR.id;
  protected readonly assignToMeLabel = ASSIGN_TO_ME_OPTION_LABEL;

  private objectUrls = new Set<string>();

  private itemIdentifier = "";
  protected maxNoteLength: number = CONSTANTS.CONSTRAINTS.MAX_NOTE_LENGTH;
  protected maxPhotos: number = CONSTANTS.CONSTRAINTS.MAX_PHOTOS;

  protected addRoomSheetOpen = signal(false);
  protected savingRoom = signal(false);
  /** Last real room choice, restored when the CM picks the "Add New Room" entry. */
  private lastSelectedRoomIdentifier: string | null = null;

  /** "Add New Room" is an action, so it counts as no room picked rather than a savable answer. */
  private readonly roomSelectionValidator = (control: AbstractControl): ValidationErrors | null =>
    (control.value === ADD_NEW_ROOM_AREA.identifier ? { required: true } : null);

  /** BQ1 skips the photo requirement; BQ5/BQ8 still require at least one. */
  protected readonly photosRequired = computed(() => this.bqStepDataService.bqStep() !== "BQ1");

  protected readonly quickTapNotes = ["Evaluate", "Touch up", "Repair", "Replace"];

  protected issueForm: FormGroup = new FormGroup({
    areaIdentifier: new FormControl<string | null>(this.unassignedAreaIdentifier, [Validators.required, this.roomSelectionValidator]),
    text: new FormControl("", Validators.required),
    images: new FormControl<Media[]>([]),
    externalVendorId: new FormControl<number | null>(null)
  });
  private formSubmitted = false;

  constructor(
    private dialog: Dialog,
    private modalService: UiModalService,
    private bqStepDataService: BqInspectionDiscussAndDemonstrateDataService,
    private sanitizer: DomSanitizer,
    private uuidService: UuidService,
    private blobService: BlobService,
    private toastService: UiToastService,
    private logger: LoggerService,
    protected imageCompressionPipelineService: ImageCompressionPipelineService
  ) {
    this.itemIdentifier = this.uuidService.v4();

    // untracked so a later change to this component's own inputs can't wipe the CM's in-progress edits.
    effect(() => {
      if (this.isOpen()) {
        untracked(() => this.applyOpenState());
      }
    });

    effect(() => {
      const control = this.issueForm.controls["images"];
      if (this.photosRequired()) {
        control.setValidators([c => (c.value?.length ? null : { required: true })]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });

    this.issueForm.controls["areaIdentifier"].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        if (value === ADD_NEW_ROOM_AREA.identifier) {
          this.addRoomSheetOpen.set(true);
          return;
        }

        this.lastSelectedRoomIdentifier = value;
        this.updateUnassignedRoomState(value);
      });
  }

  protected isInvalid(controlName: string): boolean {
    const control = this.issueForm.get(controlName);
    return !!(control?.invalid && (control.touched || this.formSubmitted));
  }

  // Persist the new/edited issue through BqInspectionDiscussAndDemonstrateDataService, then emit for the parent to highlight/navigate.
  private async onSubmit(mode: DiscussAndDemonstrateIssueSaveMode = "return") {
    this.formSubmitted = true;
    if (this.issueForm.invalid || this.savingMode() !== null) {
      return;
    }

    this.savingMode.set(mode);
    const issueForm = this.issueForm.value;
    const issue = this.issue();
    const newIssue = !issue;

    const calculateDueDate = (): Date => {
      const dueDate = new Date();
      let weekdaysAdded = 0;

      while (weekdaysAdded < 2) {
        dueDate.setDate(dueDate.getDate() + 1);
        const dayOfWeek = dueDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          weekdaysAdded += 1;
        }
      }

      return dueDate;
    };

    const savedIssue: JobIssue = {
      jobId: this.bqStepDataService.job()?.id ?? 0,
      lotAreaIdentifier: this.bqStepDataService.toSavedAreaIdentifier(issueForm.areaIdentifier),
      text: issueForm.text,
      syncStatus: SyncableStatus.Pending,
      isSelfAssigned: false,
      isComplete: false,
      updatedAt: new Date(),
      identifier: this.itemIdentifier,
      dueDate: calculateDueDate(),
      createdAt: new Date(),
      isConfirmedByHomeowner: false
    };

    if (issue) {
      savedIssue.id = issue.id;
    }

    await this.imageCompressionPipelineService.waitForCompletion();
    const mediaToSave: Media[] = this.addedImages().map(image => {
      const { file: _file, ...media } = image as MediaForCompression;
      return media;
    });

    const result = await this.bqStepDataService.saveIssue({
      issue: savedIssue,
      addedImages: mediaToSave,
      externalVendorId: issueForm.externalVendorId,
      newIssue
    });

    this.saved.emit({ issue: result, newIssue, saveMode: mode });

    if (mode === "addAnother") {
      this.toastService.success("Item Saved");
      this.resetFormData(issueForm.areaIdentifier ?? undefined);
    } else {
      this.resetFormData();
    }
    this.savingMode.set(null);
  }

  /** Stacked footer actions; the delete action only exists when editing an existing item. */
  protected get footerButtons(): VerticalButton[] {
    const isCompressing = this.imageCompressionPipelineService.isCompressingImages();
    const savingMode = this.savingMode();
    const busy = isCompressing || savingMode !== null;

    let saveLabel = this.issue() ? "Save" : "Add New Item";
    if (isCompressing) {
      saveLabel = "Optimizing Images...";
    } else if (savingMode === "return") {
      saveLabel = "Saving...";
    }

    const buttons: VerticalButton[] = [
      {
        label: saveLabel,
        severity: "contrast",
        disabled: busy,
        styleClass: "bqa-primary-btn",
        action: () => this.onSubmit("return"),
        testId: "discuss-demonstrate-btn-save-issue",
        loading: savingMode === "return"
      }
    ];

    if (this.issue()) {
      buttons.push({
        label: "Delete Item",
        severity: "danger",
        outlined: true,
        styleClass: "bqa-danger-btn",
        action: () => this.onDelete(),
        testId: "discuss-demonstrate-btn-delete-issue"
      });
    } else {
      let addAnotherLabel = "Save & Add Another Issue";
      if (isCompressing) {
        addAnotherLabel = "Optimizing Images...";
      } else if (savingMode === "addAnother") {
        addAnotherLabel = "Saving...";
      }

      buttons.push({
        label: addAnotherLabel,
        severity: "contrast",
        outlined: true,
        disabled: busy,
        styleClass: "bqa-primary-btn",
        action: () => this.onSubmit("addAnother"),
        testId: "discuss-demonstrate-btn-save-add-another-issue",
        loading: savingMode === "addAnother"
      });
    }

    buttons.push({
      label: "Cancel",
      severity: "secondary",
      text: true,
      styleClass: "bqa-secondary-btn",
      action: () => this.onDismiss(),
      testId: "discuss-demonstrate-btn-cancel-issue"
    });

    return buttons;
  }

  private onDismiss() {
    this.isOpen.set(false);
    this.closed.emit();
    this.resetFormData();
  }

  /** The sheet closes itself on swipe-down/overlay click, so mirror that into the parent's visibility. */
  protected onSheetOpenChange(isOpen: boolean) {
    if (!isOpen && this.isOpen()) {
      this.onDismiss();
    }
  }

  private resetFormData(preserveAreaIdentifier?: string) {
    this.formSubmitted = false;
    this.itemIdentifier = this.uuidService.v4();
    this.issueForm.reset({ areaIdentifier: this.unassignedAreaIdentifier, text: "", images: [], externalVendorId: null });
    this.addedImages.set([]);
    this.existingImages.set([]);
    this.annotationAnchors.set(new Map());

    if (this.issue()) {
      return;
    }

    // "Save & Add Another" keeps logging issues to the room just used, rather than falling back to the job's last-used room.
    if (preserveAreaIdentifier && this.areas().some(area => area.identifier === preserveAreaIdentifier)) {
      this.issueForm.patchValue({ areaIdentifier: preserveAreaIdentifier });
      this.updateUnassignedRoomState(preserveAreaIdentifier);
      return;
    }

    this.applyDefaultRoomSelection();
  }

  protected openImagesModal(index: number) {
    this.dialog.open<string>(ImagesModalComponent, {
      data: {
        images: this.galleryItems(),
        index,
        isIssue: true,
        onDeleted: (fileIdentifier: string) => this.handleImageDeleted(fileIdentifier),
        onAnnotated: (fileIdentifier: string, file: File) => this.handleImageAnnotated(fileIdentifier, file),
        backdropClass: "images-modal-backdrop"
      }
    });
  }

  private async handleImageAnnotated(originalFileIdentifier: string, annotatedFile: File): Promise<string | undefined> {
    const savedOriginal = this.existingImages().find(img => img.fileIdentifier === originalFileIdentifier);
    const stagedIndex = this.addedImages().findIndex(img => img.fileIdentifier === originalFileIdentifier);
    // Saved photos live in a separate list, so their copy is anchored to the original's id to keep its slot.
    const anchor = savedOriginal?.id ?? this.annotationAnchors().get(originalFileIdentifier);

    this.handleImageDeleted(originalFileIdentifier);
    const [added] = await this.onPhotosSelected([annotatedFile], stagedIndex);
    if (added && anchor !== undefined) {
      this.annotationAnchors.update(anchors => new Map(anchors).set(added.fileIdentifier, anchor));
      this.syncImagesControl();
    }
    return added?.fileIdentifier;
  }

  protected async onPhotosSelected(files: File[], insertIndex?: number): Promise<Media[]> {
    if (files.length === 0) {
      return [];
    }

    const mediaRecords = await this.imageCompressionPipelineService.encode(files, this.itemIdentifier);

    this.addedImages.set(insertAt(this.addedImages(), mediaRecords, insertIndex));

    this.syncImagesControl();

    this.imageCompressionPipelineService.compress(mediaRecords, async compressedMediaRecords => {
      const updatedImages = this.addedImages().map(originalImage => {
        const compressedImage = compressedMediaRecords.find(m => m.fileIdentifier === originalImage.fileIdentifier);
        if (compressedImage) {
          const updatedRecord: Media = {
            ...originalImage,
            base64Image: compressedImage.base64Image,
            size: compressedImage.size,
            mimeType: compressedImage.mimeType
          };
          return updatedRecord;
        }
        return originalImage;
      });
      this.addedImages.set(updatedImages);
      this.syncImagesControl();
    });

    return mediaRecords;
  }

  private handleImageDeleted(fileIdentifier: string): void {
    const wasInExisting = this.existingImages().some(img => img.fileIdentifier === fileIdentifier);
    const wasInAdded = this.addedImages().some(img => img.fileIdentifier === fileIdentifier);

    if (!wasInExisting && !wasInAdded) {
      return;
    }

    if (wasInExisting) {
      this.existingImages.set(
        this.existingImages().filter(img => img.fileIdentifier !== fileIdentifier)
      );
    }

    if (wasInAdded) {
      this.addedImages.set(
        this.addedImages().filter(img => img.fileIdentifier !== fileIdentifier)
      );
      if (this.annotationAnchors().has(fileIdentifier)) {
        this.annotationAnchors.update(anchors => {
          const remaining = new Map(anchors);
          remaining.delete(fileIdentifier);
          return remaining;
        });
      }
    }

    this.syncImagesControl(true);
  }

  private syncImagesControl(markAsTouched = false): void {
    const imagesControl = this.issueForm.get("images")!;
    imagesControl.setValue(this.allImagesRaw(), { emitEvent: false });
    if (markAsTouched) {
      imagesControl.markAsTouched();
    }
  }

  /** Runs whenever the drawer opens: populates the form for an existing issue, or applies the new-issue defaults. */
  private applyOpenState(): void {
    const issue = this.issue();
    if (!issue) {
      this.applyDefaultRoomSelection();
      return;
    }

    this.itemIdentifier = issue.identifier;
    const existingImages = (this.bqStepDataService.media() ?? [])
      .filter(m => m.parentIdentifier === this.itemIdentifier && !m.deleted);
    this.addedImages.set([]);
    this.existingImages.set(existingImages);
    this.annotationAnchors.set(new Map());

    const assignment = this.bqStepDataService.getVendorAssignmentForIssue(issue);
    const vendor = assignment
      ? this.vendors().find(v => v.id === assignment.externalVendorId)
      : null;

    this.issueForm.setValue({
      areaIdentifier: this.bqStepDataService.toFormAreaIdentifier(issue.lotAreaIdentifier),
      text: issue.text,
      images: this.existingImages(),
      externalVendorId: vendor ? vendor.id : null
    });
    this.updateUnassignedRoomState(this.issueForm.get("areaIdentifier")?.value);
  }

  /** New items default to the last assigned room, or "Unassigned Room" when none is available. */
  private applyDefaultRoomSelection() {
    const defaultArea = this.initialLotAreaIdentifier();
    if (defaultArea && this.areas().some(area => area.identifier === defaultArea)) {
      this.issueForm.patchValue({ areaIdentifier: defaultArea });
      this.updateUnassignedRoomState(defaultArea);
      return;
    }

    this.issueForm.patchValue({ areaIdentifier: this.unassignedAreaIdentifier });
    this.updateUnassignedRoomState(this.unassignedAreaIdentifier);
  }

  private updateUnassignedRoomState(areaIdentifier: string | null | undefined) {
    this.displayUnassignedRoomMessage = this.bqStepDataService.isUnassignedArea(areaIdentifier);
  }

  /** Restores the previous room when the CM backs out of adding one. */
  protected onAddRoomSheetVisibleChange(isVisible: boolean): void {
    this.addRoomSheetOpen.set(isVisible);

    if (!isVisible && this.issueForm.controls["areaIdentifier"].value === ADD_NEW_ROOM_AREA.identifier) {
      this.issueForm.controls["areaIdentifier"].setValue(this.lastSelectedRoomIdentifier);
    }
  }

  /** Creates a lot room from the Add a Room sheet; it stays available to this job's later BQ steps. */
  protected async onRoomSubmitted(details: AddAreaSubmit): Promise<void> {
    this.savingRoom.set(true);

    try {
      const newRoom = await this.bqStepDataService.addNewRoomForIssueAssignment(details);
      this.issueForm.controls["areaIdentifier"].setValue(newRoom.identifier);
      this.addRoomSheetOpen.set(false);
      this.toastService.success("Room Added");
    } catch (error) {
      this.logger.error(error);
      this.toastService.error("Unable to add the room. Please try again.");
    } finally {
      this.savingRoom.set(false);
    }
  }

  private createObjectUrlAndTrack(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);
    return url;
  }

  private onDelete() {
    const issue = this.issue();
    if (!issue) {
      return;
    }

    this.modalService.showModal({
      icon: ModalIcon.None,
      header: "Delete Item",
      subHeader: "Are you sure you want to proceed?",
      message: "This will permanently delete this item. This action cannot be undone.",
      cancelLabel: "No",
      onCancel: () => { },
      actionLabel: "Yes",
      onAction: () => this.deleteIssue(),
      isDestructive: true
    });
  }

  private async deleteIssue(): Promise<void> {
    const issue = this.issue();
    if (!issue) {
      return;
    }

    await this.bqStepDataService.deleteIssue(issue);
    this.deleted.emit();
    this.resetFormData();
  }
}
